/**
 * walkthroughs: every guided tour the app can run.
 *
 * "intro" is the getting-started tour (lib/tourSteps, the one a new account
 * meets). The six ADVANCED walkthroughs (owner direction, 2026-09-11) use the
 * same engine and the same card, one per feature area, and are started from
 * the Walkthroughs page. Each step is a spotlight on a real control
 * (data-testid) or a plain card; a control that is not on screen (needs a
 * friend, needs Pro, needs a scan) degrades to the card with the same copy,
 * so a walkthrough never stalls on an empty account.
 *
 * Copy rules: no em dashes, "Nexus Dex" never a bare "dex", one idea a step.
 */
import type { ComponentType, CSSProperties } from "react";
import { ArrowLeftRight, Compass, Heart, Search, Swords, Users, LayoutGrid } from "lucide-react";
import { TOUR_STEPS, type TourStep } from "@/lib/tourSteps";
import { queryClient } from "@/lib/queryClient";
import { listMyPokemon } from "@/lib/myPokemonStore";
import { bottomSlotOption, readBottomSlot } from "@/lib/bottomSlotPref";

export interface Walkthrough {
  key: string;
  title: string;
  /** What the walk covers, one line. */
  blurb: string;
  Icon: ComponentType<{ className?: string; style?: CSSProperties; "aria-hidden"?: boolean | "true" | "false" }>;
  steps: readonly TourStep[];
}

/* ------------------------------------------------------------ resolvers -- */

type FriendsCache = { friends?: Array<{ friend?: { id: number } | null }> };

function firstFriendId(): number | null {
  const data = queryClient.getQueryData<FriendsCache>(["/api/friends"]);
  const id = data?.friends?.map((r) => r.friend?.id).find((v): v is number => typeof v === "number");
  return id ?? null;
}

/**
 * A route that depends on live data. Null means "nothing to open": the step
 * stays where it is and shows as a card (its copy says what is missing).
 */
export function resolveWalkthroughRoute(from: NonNullable<TourStep["routeFrom"]>): string | null {
  switch (from) {
    case "friend-compare": {
      const id = firstFriendId();
      return id ? `/friends/${id}/compare` : null;
    }
    case "friend-trades": {
      const id = firstFriendId();
      return id ? `/friends/${id}/trades` : null;
    }
    case "first-my-pokemon": {
      let first: string | undefined;
      try {
        first = listMyPokemon()[0]?.id;
      } catch {
        first = undefined;
      }
      return first ? `/my-pokemon/${first}` : null;
    }
    default:
      return null;
  }
}

/**
 * A spotlight resolved from device state at activation (TourStep.targetFrom).
 * Returns the data-testid to look for.
 */
export function resolveWalkthroughTarget(from: NonNullable<TourStep["targetFrom"]>): string | null {
  switch (from) {
    case "bottom-slot-tab": {
      try {
        return bottomSlotOption(readBottomSlot()).testid;
      } catch {
        return null;
      }
    }
    default:
      return null;
  }
}

/* -------------------------------------------------------------- scripts -- */

const FRIENDS: readonly TourStep[] = [
  {
    key: "friends-intro",
    kind: "card",
    route: "/friends",
    eyebrow: "Friends 1 of 9",
    title: "Friends, priority and Compare",
    body: "Nexus Dex is better with friends: every trade match, every head-to-head and every wish list starts here.",
    bullets: ["Add a friend by code, trainer name or QR", "Pin the ones whose trades matter most", "Compare any two Nexus Dex collections head to head"],
  },
  {
    key: "friends-add",
    kind: "spotlight",
    route: "/friends",
    target: "button-add-a-friend",
    eyebrow: "Friends 2 of 9",
    title: "Add a friend",
    body: "Three ways in: their friend code, their trainer name, or scan their QR. A request goes out and they accept in their app.",
    bullets: ["Basic has three friend slots, Pro is unlimited"],
  },
  {
    key: "friends-qr",
    kind: "spotlight",
    route: "/friends",
    target: "button-show-my-qr",
    eyebrow: "Friends 3 of 9",
    title: "Your QR and invite link",
    body: "Show this to a friend in person, or share the invite link. Two friends who join and scan give you both seven days of Pro.",
  },
  {
    key: "friends-list",
    kind: "spotlight",
    route: "/friends",
    target: "section-live-friends",
    eyebrow: "Friends 4 of 9",
    title: "Your friends",
    body: "One row per friend with the head-to-head bar. Tap a row to compare, or tap a dex pill to jump straight to that dex type.",
  },
  {
    key: "friends-manage",
    kind: "spotlight",
    route: "/friends",
    target: "button-manage-friends",
    eyebrow: "Friends 5 of 9",
    title: "Manage",
    body: "Sorting, pinning and removing live on their own screen so the list itself stays clean.",
  },
  {
    key: "friends-priority",
    kind: "spotlight",
    route: "/friends/manage",
    target: "friends-manage-list",
    eyebrow: "Friends 6 of 9",
    title: "Priority friends",
    body: "Tap the star on a friend and their trades come first on your Profile and in every event card. Up to three stars with Pro.",
    bullets: ["Edit lets you remove a friend", "Sort by name, by trades, or by who joined last"],
    scrollBlock: "start",
  },
  {
    key: "friends-compare",
    kind: "spotlight",
    routeFrom: "friend-compare",
    target: "section-trainer-compare",
    eyebrow: "Friends 7 of 9",
    title: "Compare, head to head",
    body: "You against them, dex by dex. Expand the bar for every dex type. If you have no friends yet, this is what opens the first time you tap a friend row.",
    scrollBlock: "start",
  },
  {
    key: "friends-history",
    kind: "spotlight",
    routeFrom: "friend-compare",
    target: "section-dex-history",
    eyebrow: "Friends 8 of 9",
    title: "The history chart",
    body: "How both collections grew over the last weeks, with every completed trade marked on the line.",
  },
  {
    key: "friends-to-trades",
    kind: "spotlight",
    routeFrom: "friend-compare",
    target: "button-compare-open-trades",
    eyebrow: "Friends 9 of 9",
    title: "On to trades",
    body: "Every compare ends here: the Trade Hub for this friend. The trades walkthrough picks up from that button.",
  },
];

const TRADES: readonly TourStep[] = [
  {
    key: "trades-intro",
    kind: "card",
    routeFrom: "friend-trades",
    eyebrow: "Trades 1 of 10",
    title: "From potential to completed",
    body: "The Trade Hub is three panes: Potential (what could be swapped), Proposed (what you have asked for), Completed (what happened). This walk follows one trade through all three.",
    bullets: ["Needs one friend, and both of you scanned"],
  },
  {
    key: "trades-tabs",
    kind: "spotlight",
    routeFrom: "friend-trades",
    press: "trade-hub-tab-potential",
    target: "trade-hub-tabs",
    eyebrow: "Trades 2 of 10",
    title: "The three panes",
    body: "Swipe or tap between them. The counts show how many trades sit in each.",
    padding: 6,
  },
  {
    key: "trades-variant",
    kind: "spotlight",
    routeFrom: "friend-trades",
    press: "trade-hub-tab-potential",
    target: "section-filters",
    eyebrow: "Trades 3 of 10",
    title: "Pick the dex type",
    body: "Main, Shiny, Lucky, XXL and the rest: each dex type has its own set of matches. Filters below narrow by region, type and sort.",
    scrollBlock: "start",
  },
  {
    key: "trades-you-have",
    kind: "spotlight",
    routeFrom: "friend-trades",
    press: "trade-hub-tab-potential",
    target: "section-difference-grid",
    eyebrow: "Trades 4 of 10",
    title: "You have, they don't",
    body: "What you could give. Tap a tile to earmark it. Basic sees the first three per friend, Pro sees every match.",
    scrollBlock: "start",
  },
  {
    key: "trades-they-have",
    kind: "spotlight",
    routeFrom: "friend-trades",
    press: "trade-hub-tab-potential",
    target: "section-they-have",
    eyebrow: "Trades 5 of 10",
    title: "They have, you don't",
    body: "What you could get. Earmark one here too and you have both halves of a trade.",
    scrollBlock: "start",
  },
  {
    key: "trades-list",
    kind: "spotlight",
    routeFrom: "friend-trades",
    press: "trade-hub-tab-potential",
    target: "trade-list-block",
    eyebrow: "Trades 6 of 10",
    title: "Your trade list",
    body: "Everything you earmarked, give and get, in one place. Untick to drop one; removed trades wait in the pile underneath.",
    scrollBlock: "start",
  },
  {
    key: "trades-match",
    kind: "spotlight",
    routeFrom: "friend-trades",
    // The hub seeds its pane from the URL once, on mount; a `?pane=` hash
    // change never switched it (2026-09-19). Pressing the tab does.
    press: "trade-hub-tab-proposed",
    target: "section-trade-match",
    eyebrow: "Trades 7 of 10",
    title: "The trade match",
    body: "Proposed pane: your give and their get side by side. Ask to trade sends it to their app; Share card makes an image for any chat.",
    scrollBlock: "start",
  },
  {
    key: "trades-ask",
    kind: "spotlight",
    routeFrom: "friend-trades",
    // The hub seeds its pane from the URL once, on mount; a `?pane=` hash
    // change never switched it (2026-09-19). Pressing the tab does.
    press: "trade-hub-tab-proposed",
    target: "button-propose-trade",
    eyebrow: "Trades 8 of 10",
    title: "Ask to trade",
    body: "One tap and the proposal lands on their Proposed pane with Agree and Decline.",
  },
  {
    key: "trades-agree",
    kind: "spotlight",
    routeFrom: "friend-trades",
    // The hub seeds its pane from the URL once, on mount; a `?pane=` hash
    // change never switched it (2026-09-19). Pressing the tab does.
    press: "trade-hub-tab-proposed",
    target: "section-trade-proposals",
    eyebrow: "Trades 9 of 10",
    title: "Agree completes it",
    body: "When your friend taps Agree the trade is done: both Nexus Dex collections update and the trade files itself under Completed.",
    scrollBlock: "start",
  },
  {
    key: "trades-completed",
    kind: "spotlight",
    routeFrom: "friend-trades",
    press: "trade-hub-tab-completed",
    target: "completed-list-section",
    eyebrow: "Trades 10 of 10",
    title: "Completed",
    body: "The record of every trade with this friend. Each one also shows as a marker on the Compare history chart.",
    scrollBlock: "start",
  },
];

const MY_POKEMON: readonly TourStep[] = [
  {
    key: "mine-intro",
    kind: "card",
    route: "/my-pokemon",
    eyebrow: "My Pokémon 1 of 9",
    title: "My Pokémon, share cards and Box Cleanup",
    body: "Scan your Pokémon and Nexus Dex keeps the IVs, ranks and moves. From there: share a card, rename as you go, and clear the box without losing a keeper.",
  },
  {
    key: "mine-scan",
    kind: "spotlight",
    route: "/my-pokemon",
    target: "my-pokemon-live-scan",
    eyebrow: "My Pokémon 2 of 9",
    title: "Live Scan",
    body: "Open a Pokémon in GO with the scan running and its page is read as you look at it. Upload takes screenshots or a whole album; Quick add types one in.",
    padding: 6,
  },
  {
    key: "mine-filters",
    kind: "spotlight",
    route: "/my-pokemon",
    target: "my-pokemon-filters",
    eyebrow: "My Pokémon 3 of 9",
    title: "Search and filter",
    body: "Search by name or number, jump by letter, sort by league rank with the league balls, and filter by region, type, form and class.",
    scrollBlock: "start",
  },
  {
    key: "mine-list",
    kind: "spotlight",
    route: "/my-pokemon",
    target: "my-pokemon-list-section",
    eyebrow: "My Pokémon 4 of 9",
    title: "Your Pokémon",
    body: "One row each: CP, IVs, level, size, league ranks and moves. The share icon on a row makes a card; Select turns on bulk delete.",
    scrollBlock: "start",
  },
  {
    key: "mine-detail",
    kind: "spotlight",
    routeFrom: "first-my-pokemon",
    target: "my-pokemon-detail-hero",
    eyebrow: "My Pokémon 5 of 9",
    title: "The record page",
    body: "Everything about one Pokémon. Edit the CP or name, set shiny, lucky, size and costume, and copy a GO search for this exact one with the magnifier.",
    scrollBlock: "start",
  },
  {
    key: "mine-share",
    kind: "spotlight",
    routeFrom: "first-my-pokemon",
    target: "my-pokemon-share-card-open",
    eyebrow: "My Pokémon 6 of 9",
    title: "Share card",
    body: "A card with the art, IVs and ranks, ready for any chat or a trade proposal.",
  },
  {
    key: "mine-recs",
    kind: "spotlight",
    routeFrom: "first-my-pokemon",
    target: "my-pokemon-detail-recs",
    eyebrow: "My Pokémon 7 of 9",
    title: "Worth knowing",
    body: "What this Pokémon is good for and what to do with it: keep, power up, trade, or transfer. Pro reads the meta for every record.",
    scrollBlock: "start",
  },
  {
    key: "mine-cleanup",
    kind: "spotlight",
    route: "/cleanup",
    target: "cleanup-review-section",
    eyebrow: "My Pokémon 8 of 9",
    title: "Box Cleanup",
    body: "Worst first. Confirm the ones to go, spare the ones to keep, and Find in GO opens the exact search for each.",
    scrollBlock: "start",
  },
  {
    key: "mine-cleanup-batch",
    kind: "spotlight",
    route: "/cleanup",
    target: "cleanup-confirmed-section",
    eyebrow: "My Pokémon 9 of 9",
    title: "Transfer in one go",
    body: "Copy the search, paste it into GO, transfer the lot, then tap I transferred these so Nexus Dex stays in step. Undo is right there if you slip.",
    scrollBlock: "start",
  },
];

const SEARCH: readonly TourStep[] = [
  {
    key: "search-intro",
    kind: "card",
    route: "/trade",
    eyebrow: "Search 1 of 6",
    title: "Search strings, by example",
    body: "GO's search bar takes a language. Nexus Dex writes it for you from your real gaps. Example: every Shiny you are missing from Kanto, base stage only.",
  },
  {
    // The name-search card sits at the TOP of the page, above the three
    // steps, so it is visited first (2026-09-19: it used to come after Step
    // 3 and the walk scrolled back up the page).
    key: "search-names",
    kind: "spotlight",
    route: "/trade",
    target: "my-pokemon-name-search",
    eyebrow: "Search 2 of 6",
    title: "Search your own names",
    body: "If you rename with Nexus Dex, these searches find your keepers and your cleanup candidates by the tags in their names. The three steps below build a search from your dex gaps instead.",
    scrollBlock: "start",
  },
  {
    key: "search-step1",
    kind: "spotlight",
    route: "/trade",
    target: "step-1",
    eyebrow: "Search 3 of 6",
    title: "Step 1: choose your dex",
    body: "Pick Shiny Dex. The numbers feeding the search are the ones you have not caught shiny. Base Pokémon only trims it to what you can still evolve.",
    scrollBlock: "start",
  },
  {
    key: "search-step2",
    kind: "spotlight",
    route: "/trade",
    target: "step-2",
    eyebrow: "Search 4 of 6",
    title: "Step 2: fine-tune",
    body: "Each row is include, exclude or only. Set region to Kanto and leave the rest. Save it as a preset if you will use it again.",
    scrollBlock: "start",
  },
  {
    key: "search-step3",
    kind: "spotlight",
    route: "/trade",
    target: "step-3",
    eyebrow: "Search 5 of 6",
    title: "Step 3: copy and paste",
    body: "The finished string. Copy to Clipboard, open GO, paste it in the search bar, and the box shows exactly those Pokémon. Share sends it to a friend.",
    bullets: ["Basic sees a preview, Pro copies and shares"],
    scrollBlock: "start",
  },
  {
    key: "search-elsewhere",
    kind: "card",
    route: "/trade",
    eyebrow: "Search 6 of 6",
    title: "Searches everywhere",
    body: "The same strings appear wherever they help: a friend's wish list, the Trade Hub helper, Box Cleanup batches, and the magnifier on every Battle Hub row.",
  },
];

const BATTLE: readonly TourStep[] = [
  {
    key: "battle-intro",
    kind: "card",
    route: "/battle",
    eyebrow: "Battle Hub 1 of 8",
    title: "Battle Hub, view by view",
    body: "The current meta for PvP and raids, and how your own Pokémon stack up against it.",
  },
  {
    key: "battle-modes",
    kind: "spotlight",
    route: "/battle",
    target: "section-meta-mode",
    eyebrow: "Battle Hub 2 of 8",
    title: "Three views",
    body: "PvP for the leagues, Raids for attackers by boss, Yours for what you already hold.",
    padding: 6,
  },
  {
    key: "battle-pvp",
    kind: "spotlight",
    route: "/battle",
    press: "meta-mode-pvp",
    target: "section-filters",
    eyebrow: "Battle Hub 3 of 8",
    title: "PvP",
    body: "Great, Ultra or Master, then narrow by type and region. The list is the ranked meta with the move-sets to run.",
    scrollBlock: "start",
  },
  {
    key: "battle-compare",
    kind: "spotlight",
    route: "/battle",
    // Idempotent: the engine only presses when the target is not showing,
    // so this is a no-op going forward and puts PvP back after Raids.
    press: "meta-mode-pvp",
    target: "meta-compare-toggle-block",
    eyebrow: "Battle Hub 4 of 8",
    title: "Compare your Pokémon to the meta",
    body: "Turn it on and every row shows your best copy of that Pokémon, its moves and how close it is. Pro.",
    scrollBlock: "start",
  },
  {
    key: "battle-search",
    kind: "spotlight",
    route: "/battle",
    press: "meta-mode-pvp",
    target: "section-meta-list",
    // The copy is about the magnifier, so the ring goes on the first row's
    // magnifier rather than round the whole ranked list (2026-09-19).
    targetSelector: '[data-testid="section-meta-list"] [data-testid^="meta-search-"]',
    eyebrow: "Battle Hub 5 of 8",
    title: "The magnifier",
    body: "On any row, the magnifier copies a GO search for that Pokémon with the right move-sets, so you can arm it in one paste.",
    scrollBlock: "start",
  },
  {
    key: "battle-raids",
    kind: "spotlight",
    route: "/battle",
    press: "meta-mode-raid",
    target: "section-raid-filters",
    eyebrow: "Battle Hub 6 of 8",
    title: "Raids",
    body: "Pick a boss, or a type, and see the best attackers in order. Shadows toggle on and off. Compare works here too.",
    scrollBlock: "start",
  },
  {
    key: "battle-yours-best",
    kind: "spotlight",
    route: "/battle",
    press: "meta-mode-yours",
    target: "section-yours-best",
    eyebrow: "Battle Hub 7 of 8",
    title: "Yours: your best copies",
    body: "The strongest Pokémon you hold for each league and for raids, judged on what each one becomes. Needs a scanned collection.",
    scrollBlock: "start",
  },
  {
    key: "battle-yours-types",
    kind: "spotlight",
    route: "/battle",
    press: "meta-mode-yours",
    target: "section-yours-types",
    eyebrow: "Battle Hub 8 of 8",
    title: "Yours: the type podium",
    body: "Every type, and whether you hold its top Pokémon. Number one is always free; Pro shows two to ten, each marked yours or missing.",
    scrollBlock: "start",
  },
];

const DEX: readonly TourStep[] = [
  {
    key: "dex-intro",
    kind: "card",
    route: "/dex",
    eyebrow: "Nexus Dex 1 of 8",
    title: "Searching and filtering your Nexus Dex",
    body: "A thousand Pokémon across fourteen dex types. Here is how to get to the one you want.",
  },
  {
    key: "dex-types",
    kind: "spotlight",
    route: "/dex",
    target: "pokedex-variant-tabs",
    eyebrow: "Nexus Dex 2 of 8",
    title: "Dex types",
    body: "Tap a pill, or swipe the grid sideways, to move between Main, Shiny, Lucky and the rest. Hide the ones you do not track in Settings.",
    scrollBlock: "start",
  },
  {
    key: "dex-search",
    kind: "spotlight",
    route: "/dex",
    target: "pokedex-filter-search",
    eyebrow: "Nexus Dex 3 of 8",
    title: "Search",
    body: "A name or a dex number. The grid narrows as you type.",
    scrollBlock: "start",
  },
  {
    key: "dex-status",
    kind: "spotlight",
    route: "/dex",
    target: "pokedex-status-toggle",
    eyebrow: "Nexus Dex 4 of 8",
    title: "Caught or missing",
    body: "All, Caught or Missing. Missing plus a region is the fastest way to plan a day.",
    scrollBlock: "start",
  },
  {
    key: "dex-filters",
    kind: "spotlight",
    route: "/dex",
    press: "dex-filter-menu-toggle",
    target: "dex-filter-menu-panel",
    eyebrow: "Nexus Dex 5 of 8",
    title: "Filters",
    body: "Region, type, sort, forms, evolution line, class, and what is actually available in GO right now. Event filters appear while an event is on.",
    scrollBlock: "start",
  },
  {
    key: "dex-copy",
    kind: "spotlight",
    route: "/dex",
    target: "pokedex-copy-filtered-numbers",
    eyebrow: "Nexus Dex 6 of 8",
    title: "Copy what you filtered",
    body: "Whatever the grid shows, copy the numbers as a GO search string and find exactly those in your box. Pro.",
  },
  {
    key: "dex-region",
    kind: "spotlight",
    route: "/dex",
    target: "pokedex-next-region",
    eyebrow: "Nexus Dex 7 of 8",
    title: "Jump by region",
    body: "Next region hops the grid a generation at a time; Back to top appears once you have scrolled.",
  },
  {
    key: "dex-done",
    kind: "card",
    route: "/dex",
    eyebrow: "Nexus Dex 8 of 8",
    title: "That is the whole grid",
    body: "Tap any Pokémon for its page: every dex type it can be, its forms, and who among your friends can get it for you.",
  },
];

export const WALKTHROUGHS: readonly Walkthrough[] = [
  { key: "intro", title: "Getting started", blurb: "The tour every new trainer gets: the bottom bar, the menu, your Nexus Dex, and a live scan.", Icon: Compass, steps: TOUR_STEPS },
  { key: "friends", title: "Friends, priority and Compare", blurb: "Add friends, pin the ones that matter, and compare head to head.", Icon: Users, steps: FRIENDS },
  { key: "trades", title: "Trades, start to finish", blurb: "One trade from Potential through Proposed to Completed.", Icon: ArrowLeftRight, steps: TRADES },
  { key: "my-pokemon", title: "My Pokémon, share cards and Box Cleanup", blurb: "Scan, share, rename, and clear the box safely.", Icon: Heart, steps: MY_POKEMON },
  { key: "search", title: "Search strings, by example", blurb: "Build a GO search from your gaps and paste it in.", Icon: Search, steps: SEARCH },
  { key: "battle", title: "Battle Hub, view by view", blurb: "PvP, Raids and Yours, with the compare block and the magnifier.", Icon: Swords, steps: BATTLE },
  { key: "dex", title: "Nexus Dex: searching and filtering", blurb: "Dex types, search, status, filters and region jumps.", Icon: LayoutGrid, steps: DEX },
];

export const ADVANCED_WALKTHROUGHS: readonly Walkthrough[] = WALKTHROUGHS.filter((w) => w.key !== "intro");

export function walkthroughByKey(key: string): Walkthrough | undefined {
  return WALKTHROUGHS.find((w) => w.key === key);
}

/** The steps GuidedTour runs for a script key; unknown keys run the intro. */
export function stepsForScript(key: string): readonly TourStep[] {
  return walkthroughByKey(key)?.steps ?? TOUR_STEPS;
}
